import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import LoadingScreen from "./components/layout/LoadingScreen";
import { AuthenticatedWrapper, PrivateWrapper } from "./components/route/RouteWrappers";
import Admin from "./pages/Admin/index";
import Auto4client from "./pages/Auto4Client/index";
import Dashboard from "./pages/Dashboard/index";
import DataUploader from "./pages/DataUploader/index";
import ExportJson from "./pages/ExportJson/index";
import Geocoder from "./pages/Geocoder/index";
import Login from "./pages/Login/index";
import Logs from "./pages/Logs/index";
import LogStreamPage from "./pages/LogStreamPage/index";
import NewTask from "./pages/NewTask/index";
import NotFound from "./pages/NotFound/index";
import PreflightChecks from "./pages/PreflightChecks/index";
import Settings from "./pages/Settings/index";
import Tasks from "./pages/Tasks/index";
import SheetGenerator from "./pages/SheetGenerator/index";
import ClassificationPipeline from "./pages/ClassificationPipeline/index";
import { checkJwtAuth } from "./services/auth/authorisation";
import { darkTheme } from "./theme";

/**
 * Main application component that handles authentication state
 * and renders the application with proper routing
 *
 * @returns {JSX.Element} The rendered application
 */
export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const verifyUserAuthentication = async () => {
    console.log("Verifying user authentication");
    setIsLoading(true);
    const authResult = await checkJwtAuth();
    setIsAuthenticated(authResult);
    setIsLoading(false);
  };

  useEffect(() => {
    verifyUserAuthentication();
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  } else {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <BrowserRouter>
          <Routes>
            <Route path="*" element={<NotFound />} />
            <Route element={<AuthenticatedWrapper auth={{ isAuthenticated: isAuthenticated }} />}>
              <Route path="/login" element={<Login checkAuthUser={verifyUserAuthentication} />} />
            </Route>
            <Route element={<PrivateWrapper auth={{ isAuthenticated: isAuthenticated }} />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/export-json" element={<ExportJson />} />
              <Route path="/tasks" element={<Tasks />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/tasks/new-task" element={<NewTask />} />
              <Route path="/auto4client" element={<Auto4client />} />
              <Route path="/data-uploader" element={<DataUploader />} />
              <Route path="/logs" element={<Logs />} />
              <Route path="/logs/:logStreamName" element={<LogStreamPage />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/geocoder" element={<Geocoder />} />
              <Route path="/sheet-generator" element={<SheetGenerator />} />
              <Route path="/classification-pipeline" element={<ClassificationPipeline />} />
              <Route path="/preflight-checks" element={<PreflightChecks />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    );
  }
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<App />);
